'use client';

import React from 'react';
import { ContentComponent, ComponentBasedContent } from '../types/content-components.types.js';
// Core components
import { HeroCard } from './HeroCard.js';
import { ConceptCard } from './ConceptCard.js';
import { ComparisonGrid } from './ComparisonGrid.js';
import { Timeline } from './Timeline.js';
import { CalloutBox } from './CalloutBox.js';
import { StatShowcase } from './StatShowcase.js';
import { InteractiveReveal } from './InteractiveReveal.js';
// Visual components
import { ImageCard } from './ImageCard.js';
import { VisualDiagram } from './VisualDiagram.js';
import { BeforeAfter } from './BeforeAfter.js';
// Interactive components
import { FlipCard } from './FlipCard.js';
import { Accordion } from './Accordion.js';
import { MiniQuiz } from './MiniQuiz.js';
import { SliderInput } from './SliderInput.js';
import { Checklist } from './Checklist.js';
// Engagement components
import { QuoteCard } from './QuoteCard.js';
import { ProgressSteps } from './ProgressSteps.js';

/**
 * Main Content Renderer - Dynamically renders components based on type
 * 
 * This is the single source of truth for rendering all content components.
 * Add new component mappings here as you build more components.
 */

interface ContentRendererProps {
  content: ComponentBasedContent;
  className?: string;
  /** Show content metadata (reading time, interactivity level, etc.) - default false */
  showMetadata?: boolean;
}

export const ContentRenderer: React.FC<ContentRendererProps> = ({ content, className = '', showMetadata = false }) => {
  const renderComponent = (component: ContentComponent) => {
    switch (component.type) {
      case 'hero_card':
        return <HeroCard key={component.id} component={component} />;
      
      case 'concept_card':
        return <ConceptCard key={component.id} component={component} />;
      
      case 'comparison_grid':
        return <ComparisonGrid key={component.id} component={component} />;
      
      case 'timeline':
        return <Timeline key={component.id} component={component} />;
      
      case 'callout_box':
        return <CalloutBox key={component.id} component={component} />;
      
      case 'stat_showcase':
        return <StatShowcase key={component.id} component={component} />;
      
      case 'interactive_reveal':
        return <InteractiveReveal key={component.id} component={component} />;
      
      // Visual components
      case 'image_card':
        return <ImageCard key={component.id} component={component} />;
      
      case 'visual_diagram':
        return <VisualDiagram key={component.id} component={component} />;
      
      case 'before_after':
        return <BeforeAfter key={component.id} component={component} />;
      
      // Interactive components
      case 'flip_card':
        return <FlipCard key={component.id} component={component} />;
      
      case 'accordion':
        return <Accordion key={component.id} component={component} />;
      
      case 'mini_quiz':
        return <MiniQuiz key={component.id} component={component} />;
      
      case 'slider_input':
        return <SliderInput key={component.id} component={component} />;
      
      case 'checklist':
        return <Checklist key={component.id} component={component} />;
      
      // Engagement components
      case 'quote_card':
        return <QuoteCard key={component.id} component={component} />;
      
      case 'progress_indicator':
        return <ProgressSteps key={component.id} component={component} />;
      
      // Legacy/not yet implemented
      case 'quote_block':
        return <QuoteCard key={component.id} component={component as any} />;
      
      default:
        // Fallback for unknown component types
        console.warn(`Unknown component type: ${(component as any).type}`);
        return (
          <div key={component.id} className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
            <p className="text-sm text-yellow-800">
              ⚠️ Unknown component type: <code>{(component as any).type}</code>
            </p>
          </div>
        );
    }
  };

  return (
    <div className={`w-full space-y-6 ${className}`}>
      {content.components.map((component) => renderComponent(component))}
      
      {/* Metadata display - only shown when explicitly enabled (e.g., in admin preview) */}
      {content.metadata && showMetadata && (
        <div className="mt-8 p-3 bg-gray-100 rounded-lg border border-gray-300 text-xs text-gray-600">
          <div className="font-semibold mb-1">Content Metadata:</div>
          <div>Reading Time: ~{content.metadata.estimatedReadingTime}s</div>
          <div>Interactivity: {content.metadata.interactivityLevel}</div>
          <div>Learning Mode: {content.metadata.primaryLearningMode}</div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to check if content is component-based
 */
export const isComponentBasedContent = (content: any): content is ComponentBasedContent => {
  return (
    content &&
    typeof content === 'object' &&
    'version' in content &&
    'components' in content &&
    Array.isArray(content.components)
  );
};

/**
 * Note: Consuming applications should import the animations.css:
 * import '@twygs/shared/micro-learning/styles/animations.css';
 */

